import { GroundType } from 'models/ground';
import React, { useMemo } from 'react';
import {
  Circle, Group, Line, Path,
} from 'react-konva';
import {
  DEFAULT_PITCH_LINE_COLOR,
  DEFAULT_PITCH_STROKEWIDTH,
  FOOTBALL_CENTER_RADIUS,
  FOOTBALL_DISK_OFFSET,
  FOOTBALL_DISK_RADIUS,
  FOOTBALL_GOAL_AREA_HEIGHT,
  FOOTBALL_GOAL_AREA_HEIGHT_SMALL,
  FOOTBALL_GOAL_AREA_WIDTH,
  FOOTBALL_GOAL_AREA_WIDTH_SMALL,
} from '../constants';
import { describeArc, flipX, flipY } from '../utils';
import Vertices from '../Vertices';
import { GroundGeometry } from '../../../../types/webPlayer';

function Area(props: {
  vertexA: { x: number; y: number };
  vertexC: { x: number; y: number };
  height: number;
  center: { x: number; y: number };
  scale: number;
  flipped?: boolean;
}) {
  const {
    vertexA, vertexC, height, scale, flipped, center,
  } = props;

  const scaledCenterRadius = FOOTBALL_CENTER_RADIUS * scale;
  const scaledAreaWidth = FOOTBALL_GOAL_AREA_WIDTH * scale;
  const scaledAreaHeight = FOOTBALL_GOAL_AREA_HEIGHT * scale;
  const scaledAreaWidthSm = FOOTBALL_GOAL_AREA_WIDTH_SMALL * scale;
  const scaledAreaHeightSm = FOOTBALL_GOAL_AREA_HEIGHT_SMALL * scale;
  const scaledDiskOffset = FOOTBALL_DISK_OFFSET * scale;
  const scaledDiskRadius = FOOTBALL_DISK_RADIUS * scale;
  const scaledCornerRadius = scale;

  const isReversed = vertexA.y < vertexC.y;

  const geometry = useMemo(() => ({
    arc1: {
      x: flipped
        ? vertexC.x
        : vertexA.x,
      y: flipped
        ? vertexC.y + height // (isReversed ? vertexC.y : vertexC.y + scaledCornerRadius)
        : vertexA.y,
      startAngle: flipped ? (isReversed ? -90 : 90) : (isReversed ? -90 : 0),
      endAngle: flipped ? (isReversed ? 0 : 0) : (isReversed ? -180 : -90),
      radius: scaledCornerRadius < 0 ? scaledCornerRadius * -1 : scaledCornerRadius,
    },
    arc2: {
      x: flipped
        ? vertexC.x + scaledCornerRadius
        : vertexA.x,
      y: flipped
        ? vertexC.y + scaledCornerRadius
        : vertexA.y - height,
      startAngle: flipped ? (isReversed ? 90 : 270) : (isReversed ? 90 : 270),
      endAngle: flipped ? (isReversed ? 0 : 0) : (isReversed ? 180 : 180),
      radius: scaledCornerRadius < 0 ? scaledCornerRadius * -1 : scaledCornerRadius,
    },
    line1: flipped
      ? [
        flipX({ x: vertexA.x, y: center.y + scaledAreaHeight / 2 }, center),
        flipY({ x: vertexA.x, y: center.y + scaledAreaHeight / 2 }, center),
        flipX({ x: vertexA.x - scaledAreaWidth, y: center.y + scaledAreaHeight / 2 }, center),
        flipY({ x: vertexA.x - scaledAreaWidth, y: center.y + scaledAreaHeight / 2 }, center),
        flipX({ x: vertexA.x - scaledAreaWidth, y: center.y - scaledAreaHeight / 2 }, center),
        flipY({ x: vertexA.x - scaledAreaWidth, y: center.y - scaledAreaHeight / 2 }, center),
        flipX({ x: vertexA.x, y: center.y - scaledAreaHeight / 2 }, center),
        flipY({ x: vertexA.x, y: center.y - scaledAreaHeight / 2 }, center),
      ]
      : [
        vertexA.x, center.y + scaledAreaHeight / 2,
        vertexA.x - scaledAreaWidth, center.y + scaledAreaHeight / 2,
        vertexA.x - scaledAreaWidth, center.y - scaledAreaHeight / 2,
        vertexA.x, center.y - scaledAreaHeight / 2,
      ],
    line2: flipped
      ? [
        flipX({ x: vertexA.x, y: center.y + scaledAreaHeightSm / 2 }, center),
        flipY({ x: vertexA.x, y: center.y + scaledAreaHeightSm / 2 }, center),
        flipX({ x: vertexA.x - scaledAreaWidthSm, y: center.y + scaledAreaHeightSm / 2 }, center),
        flipY({ x: vertexA.x - scaledAreaWidthSm, y: center.y + scaledAreaHeightSm / 2 }, center),
        flipX({ x: vertexA.x - scaledAreaWidthSm, y: center.y - scaledAreaHeightSm / 2 }, center),
        flipY({ x: vertexA.x - scaledAreaWidthSm, y: center.y - scaledAreaHeightSm / 2 }, center),
        flipX({ x: vertexA.x, y: center.y - scaledAreaHeightSm / 2 }, center),
        flipY({ x: vertexA.x, y: center.y - scaledAreaHeightSm / 2 }, center),
      ]
      : [
        vertexA.x,
        center.y + scaledAreaHeightSm / 2,
        vertexA.x - scaledAreaWidthSm,
        center.y + scaledAreaHeightSm / 2,
        vertexA.x - scaledAreaWidthSm,
        center.y - scaledAreaHeightSm / 2,
        vertexA.x,
        center.y - scaledAreaHeightSm / 2,
      ],
    circle1: {
      x: flipped ? vertexC.x + scaledDiskOffset : vertexA.x - scaledDiskOffset,
      y: center.y,
      radius: scaledDiskRadius < 0 ? scaledDiskRadius * -1 : scaledDiskRadius,
    },
    path: {
      x: flipped ? vertexC.x + scaledDiskOffset : vertexA.x - scaledDiskOffset,
      y: center.y,
      startAngle: flipped ? -143 : 37,
      endAngle: flipped ? -37 : 143,
      radius: scaledCenterRadius < 0 ? scaledCenterRadius * -1 : scaledCenterRadius,
    },
  }), [vertexA.x, vertexA.y, vertexC.x, vertexC.y, flipped, scaledCornerRadius, height, center]);

  const paths = useMemo(() => ({
    path1: describeArc(/* corner */
      geometry.arc1.x,
      geometry.arc1.y,
      geometry.arc1.radius,
      geometry.arc1.startAngle,
      geometry.arc1.endAngle,
    ),
    path2: describeArc(/* disco */
      geometry.arc2.x,
      geometry.arc2.y,
      geometry.arc2.radius,
      geometry.arc2.startAngle,
      geometry.arc2.endAngle,
    ),
    path3: describeArc(
      geometry.path.x,
      geometry.path.y,
      geometry.path.radius,
      geometry.path.startAngle,
      geometry.path.endAngle,
    ),
  }), [geometry]);

  return (
    <Group>
      <Path data={paths.path1} stroke={DEFAULT_PITCH_LINE_COLOR} strokeWidth={DEFAULT_PITCH_STROKEWIDTH} />
      <Path data={paths.path2} stroke={DEFAULT_PITCH_LINE_COLOR} strokeWidth={DEFAULT_PITCH_STROKEWIDTH} />

      <Line points={geometry.line1} stroke={DEFAULT_PITCH_LINE_COLOR} strokeWidth={DEFAULT_PITCH_STROKEWIDTH} />
      <Line points={geometry.line2} stroke={DEFAULT_PITCH_LINE_COLOR} strokeWidth={DEFAULT_PITCH_STROKEWIDTH} />
      <Circle
        x={geometry.circle1.x}
        y={geometry.circle1.y}
        radius={geometry.circle1.radius}
        stroke={DEFAULT_PITCH_LINE_COLOR}
        strokeWidth={DEFAULT_PITCH_STROKEWIDTH}
        fill={DEFAULT_PITCH_LINE_COLOR}
      />
      <Path data={paths.path3} stroke={DEFAULT_PITCH_LINE_COLOR} strokeWidth={DEFAULT_PITCH_STROKEWIDTH} />
    </Group>
  );
}

export default function Football(props: {
  groundData: GroundType;
  scale: number;
  vertices: boolean;
  groundGeometry: GroundGeometry;
}) {
  const {
    groundData, vertices, groundGeometry, scale,
  } = props;

  const {dims, vertex, center} = groundGeometry;
  const {width, height} = dims;

  const {
    vertexA,
    vertexB,
    vertexC,
    vertexD,
  } = vertex;

  const geometry = useMemo(() => {
    const scaledCenterRadius = FOOTBALL_CENTER_RADIUS * scale;
    const scaledDiskRadius = FOOTBALL_DISK_RADIUS * scale;

    return {
      vertex,
      scale,
      center,
      width,
      height,
      line1: [
        vertexA.x, vertexA.y,
        vertexB.x, vertexB.y,
        vertexC.x, vertexC.y,
        vertexD.x, vertexD.y,
      ],
      line2: [
        center.x, vertexA.y,
        center.x, vertexC.y,
      ],
      circle1: {
        x: center.x,
        y: center.y,
        radius: scaledCenterRadius < 0 ? scaledCenterRadius * -1 : scaledCenterRadius,
      },
      circle2: {
        x: center.x,
        y: center.y,
        radius: scaledDiskRadius < 0 ? scaledDiskRadius * -1 : scaledDiskRadius,
      },
    };
  }, [groundGeometry]);

  return (
    <Group>
      {/* border */}
      <Line
        fill={groundData.groundSurfaceColor ? groundData.groundSurfaceColor : '#00000000'}
        points={geometry.line1}
        stroke={DEFAULT_PITCH_LINE_COLOR}
        strokeWidth={DEFAULT_PITCH_STROKEWIDTH}
      />
      <Line
        fill={groundData.groundSurfaceColor ? groundData.groundSurfaceColor : '#00000000'}
        points={geometry.line1}
        stroke={DEFAULT_PITCH_LINE_COLOR}
        strokeWidth={DEFAULT_PITCH_STROKEWIDTH}
        closed
      />
      {/* left area */}
      <Area
        vertexA={geometry.vertex.vertexA}
        vertexC={geometry.vertex.vertexC}
        height={geometry.height}
        center={geometry.center}
        scale={geometry.scale}
      />
      {/* rightarea */}
      <Area
        vertexA={geometry.vertex.vertexA}
        vertexC={geometry.vertex.vertexC}
        height={geometry.height}
        center={geometry.center}
        scale={geometry.scale}
        flipped
      />
      {/* halfcourt */}
      <Line
        points={geometry.line2}
        stroke={DEFAULT_PITCH_LINE_COLOR}
        strokeWidth={DEFAULT_PITCH_STROKEWIDTH}
      />
      <Circle
        x={geometry.circle1.x}
        y={geometry.circle1.y}
        radius={geometry.circle1.radius}
        stroke={DEFAULT_PITCH_LINE_COLOR}
        strokeWidth={DEFAULT_PITCH_STROKEWIDTH}
      />
      <Circle
        x={geometry.circle2.x}
        y={geometry.circle2.y}
        radius={geometry.circle2.radius}
        stroke={DEFAULT_PITCH_LINE_COLOR}
        strokeWidth={DEFAULT_PITCH_STROKEWIDTH}
        fill={DEFAULT_PITCH_LINE_COLOR}
      />

      {vertices ? <Vertices A={geometry.vertex.vertexA} C={geometry.vertex.vertexC} /> : null}
    </Group>
  );
}
